/**
 * UpdatePrice orchestration + clamp (REQ-PRICE-1; research/04 §2.2):
 * checkout-in-progress early return; EvaluateFormulaElements first;
 * FormulaParser; whole-string "NaN" → miniMumCartValue; eval;
 * Math.max(miniMumCartValue, v) clamp; ALWAYS set #priceTicker
 * data-origMoney to raw finalValue; animate only when finalValue !=
 * cpc_currentPrice (loose) and not NaN; EvaluateCalculationElements after;
 * catch-all → globalHelper_ReportError(e, "FormulaBeforeEval = " + …).
 *
 * The eval lives in formula/evaluate.ts; evaluateFormula re-declares this
 * function's v1 lowercase locals (`formula`, hoisted `msg`) so direct-eval
 * identifier capture is unchanged (ERRATA #10 / FORM-010c). Do not rename
 * locals here.
 */
import { appState } from "../state/app-state";
import { globalHelper_ReportError } from "../api/logs-error";
import { FormulaParser, evaluateFormula } from "../formula";
import { EvaluateFormulaElements } from "./formula-elements";
import { EvaluateCalculationElements } from "./calculation-elements";
import { animatePriceTicker } from "./ticker";
import {
  clearPriceFailure,
  getPriceFailure,
  recordPriceFailure,
} from "./price-failure";
import $ from "jquery";

export function UpdatePrice(): void {
  if (appState.cpc_isCheckoutInProgress) return;
  var formulaBeforeEval = "";
  try {
    var formula = appState.cpc_data.Formula;
    EvaluateFormulaElements();

    //console.log('before-formula', formula)
    formula = FormulaParser(formula);
    if (formula === "NaN") formula = appState.miniMumCartValue;
    // console.log('after-formula', formula)
    formulaBeforeEval = formula;
    var evaluatedNewPrice = evaluateFormula(formula);
    //console.log('evaluated price', evaluatedNewPrice)
    var finalValue = Math.max(appState.miniMumCartValue as any, evaluatedNewPrice);
    $("#priceTicker").attr("data-origMoney", finalValue);
    // The ticker animates asynchronously, so in the preview an Infinity would
    // land on top of the dash a moment after it was written. Suppress that in
    // the preview only. On a storefront the condition is byte-for-byte the
    // original `!isNaN`, so shoppers still see exactly what they see today —
    // "Infinity" included, which is its own separate decision.
    var canAnimate =
      appState.cpc_isPreview === true
        ? Number.isFinite(finalValue)
        : !isNaN(finalValue);
    if (finalValue != appState.cpc_currentPrice && canAnimate) {
      animatePriceTicker(finalValue);
      appState.cpc_currentPrice = finalValue;
    }
    // An unusable result never throws, so without this nothing is reported
    // anywhere. TWO shapes, and the second was missed at first: 0/0 gives NaN,
    // which leaves the ticker on the price it already had (usually 0); but
    // 10/0 gives Infinity, which is NOT NaN, so it sails through the guard
    // above and the shopper is shown the literal word "Infinity" as a price.
    // Number.isFinite covers NaN, Infinity and -Infinity in one test.
    //
    // The guard above is deliberately left on isNaN: that is existing
    // storefront behaviour and changing it would change what shoppers see.
    // This only decides what we RECORD, which drives the preview and the save.
    if (!Number.isFinite(finalValue)) {
      recordPriceFailure({
        reason: "not-a-number",
        expression: formulaBeforeEval,
      });
    } else {
      // Recovering from a failure in the preview: the ticker is showing a dash,
      // and the branch above only repaints when the value CHANGED. If the price
      // is the same one it held before the break, repaint it here or the dash
      // would stick on a calculator that is now working.
      //
      // Preview-gated: only the preview ever shows a dash, so only the preview
      // needs the repaint. A shopper's storefront must animate exactly when it
      // does today and no more.
      if (
        appState.cpc_isPreview === true &&
        getPriceFailure() !== null &&
        finalValue == appState.cpc_currentPrice
      ) {
        animatePriceTicker(finalValue);
      }
      clearPriceFailure();
    }
    EvaluateCalculationElements();
  } catch (e) {
    var msg = "FormulaBeforeEval = " + formulaBeforeEval;
    globalHelper_ReportError(e, msg);
    recordPriceFailure({
      reason: "error",
      expression: formulaBeforeEval,
      message: e instanceof Error ? e.message : String(e),
    });
  }
}
